import {Backdrop, useMediaQuery} from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import {createRef, FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {QueryObserverBaseResult} from 'react-query'
import {useDispatch} from 'react-redux'
import {useNavigate, useParams} from 'react-router'
import {Zoom} from 'swiper'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Swiper as SwiperRef} from 'swiper/types'
import {LikeButton, LikePhoto, UnLikePhoto} from 'entities/activity'
import {PhotoTypes, useChatCanViewPrivatePhotos} from 'entities/messages'
import {
  closeGallery,
  deleteGalleryPhotos,
  removeFromMyMedia,
  setCurrentImage,
  setLikePhoto,
  useGallery,
  useGalleryCursor,
  useGalleryIsPhotoList,
  useOpenStateGallery,
  useProfileMediaLikePhotos
} from 'entities/profile'
import {clearAvatar, DeleteAvatar} from 'entities/user'
import {DeletePhotos} from 'shared/api/images'
import {useVirtualScroll} from 'shared/hooks'
import {mediaVariants, names} from 'shared/lib'
import {Button} from 'shared/ui/buttons'
import {
  ArrowDown,
  ChevronsLeft,
  ChevronsRight,
  Close,
  Cross,
  Dots,
  Lock,
  PaperPlane,
  TrashOutlined
} from 'shared/ui/icons'
import {Loader} from 'shared/ui/loader'
import styles from './styles.module.scss'

type GalleryProps = {
  load?: QueryObserverBaseResult['refetch'];
  isFetching?: boolean
  isError?: boolean
  isHideLike?: boolean
}

const countSlidesPerView = 1

const updateSrc = (src: string, type: PhotoTypes, isPrivate?: boolean) => {
  if (isPrivate) return src + mediaVariants.originalLBlur

  return src + (type !== PhotoTypes.VIDEO_AVATAR ? mediaVariants.originalL : '')
}

export const Gallery: FC<GalleryProps> = ({load, isFetching, isError, isHideLike}) => {
  const isOpenGallery = useOpenStateGallery()
  const isPhotoList = useGalleryIsPhotoList()
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()
  const {galleryPhotos: photos, currentImage} = useGallery()
  const [isHiddenSideBar, setIsHiddenSideBar] = useState<boolean>(false)
  const photoContainerBoxRef = useRef<HTMLDivElement>(null)
  const galleryCursor = useGalleryCursor()
  const {id} = useParams()
  const isViewPrivatePhotos = useChatCanViewPrivatePhotos()
  const likePhotos = useProfileMediaLikePhotos()
  const [fetchingDelete, setFetchingDelete] = useState(false)
  const is768p = useMediaQuery(`(max-width: ${styles.is768p})`)
  const [swiperRef, setSwiperRef] = useState<SwiperRef | null>(null)
  const [isExtraOpen, setIsExtraOpen] = useState(false)
  const isPrivatePhoto = currentImage.type === PhotoTypes.private && !isViewPrivatePhotos
  const [isLoadingPhoto, setIsLoadingPhoto] = useState(true)

  useEffect(() => {
    setIsLoadingPhoto(true)
  }, [currentImage.src])

  const closeHandler = () => {
    setIsExtraOpen(false)
    dispatch(closeGallery())
  }

  // TODO: move to helper
  const deleteHandler = (imageId?: string) => {
    if(currentImage.type === PhotoTypes.avatar) {
      const srcSplit = currentImage.src.split('/')
      const srcMediaKey = srcSplit[srcSplit.length - 2] || ''

      setFetchingDelete(true)

      DeleteAvatar(srcMediaKey)
        .then(() => {
          dispatch(clearAvatar())
          closeHandler()
        })
        .catch(console.error)
        .finally(() => setFetchingDelete(false))

      return
    }

    if (!imageId) return
    setFetchingDelete(true)

    DeletePhotos([imageId])
      .then(() => {
        dispatch(removeFromMyMedia({photosIds: [imageId]}))
        dispatch(deleteGalleryPhotos([imageId]))

        if (photos.length === 1) return closeHandler()

        const nextImage = photos[currentImage.index + 1] ?? photos[currentImage.index - 1]
        const index = currentImage.index + 1 > photos.length - 2 ? currentImage.index - 1 : currentImage.index

        dispatch(setCurrentImage({
          src: nextImage.image.url,
          index,
          type: nextImage.type,
          id: nextImage.id,
          label: currentImage.label
        }))

        setIsExtraOpen(false)
        if (!swiperRef) return

        swiperRef.update()
        swiperRef.updateSlides()
      })
      .finally(() => setFetchingDelete(false))
  }

  useEffect(() => {
    // last photo handler
    if (isFetching || !isLoading) return

    setIsLoading(false)
    onChangeHandler(true)

  }, [isFetching, isLoading])

  // TODO: move to helper
  const onChangeHandler = useCallback(
    (isNext?: boolean) => {
      let index = currentImage.index + (isNext ? +1 : -1)
      if (index < 0) return

      if (isNext && !photos[index]) {
        if (!galleryCursor) {
          index = 0
        }
        if (galleryCursor) {
          return load && load().finally(() => setIsLoading(true))
        }
      }

      dispatch(
        setCurrentImage({
          ...currentImage,
          index,
          type: photos[index].type,
          src: photos[index].image.url,
          id: photos[index].id
        })
      )
    },
    [currentImage, photos]
  )

  const hideHandler = () => {
    setIsHiddenSideBar(!isHiddenSideBar)
  }

  // TODO: move to helper
  const likeHandler = () => {
    if (!id || !currentImage.id) return

    if (likePhotos[currentImage.id]) {
      UnLikePhoto(id, currentImage.id)
        .then(() => dispatch(setLikePhoto({idPhoto: currentImage.id!, value: false})))
        .catch((e) => {
          console.error(e)
          dispatch(setLikePhoto({idPhoto: currentImage.id!, value: true}))
        })
      return
    }

    LikePhoto(id, currentImage.id)
      .then(() => dispatch(setLikePhoto({idPhoto: currentImage.id!, value: true})))
      .catch((e) => {
        console.error(e)
        dispatch(setLikePhoto({idPhoto: currentImage.id!, value: false}))
      })
  }

  const onSlideChange = (swiper: SwiperRef) => {
    const photo = photos[swiper.realIndex]
    if (!photo) return

    dispatch(setCurrentImage({
      ...currentImage,
      index: swiper.realIndex,
      type: photo.type,
      src: photo.image.url,
      id: photo.id
    }))
  }

  const onReachEnd = () => galleryCursor && load && load()

  const renderedSlides = useMemo(() => {
    const images = (photos?.find((item) => item.id === currentImage.id)
      ? photos.map((item) => ({id: item.id, src: item.image.url, type: item.type}))
      : [{id: currentImage.id, src: currentImage.src, type: currentImage.type}])

    return images.map((item) => (
      <SwiperSlide key={item.id}>
        <div className={styles.slide}>
          <Loader size={20} innerColor='white' className={styles.slideLoader} />
          <div className='swiper-zoom-container'>
            <picture className={styles.picture}>
              <img
                className={styles.photo}
                src={updateSrc(item.src, item.type, item.type === PhotoTypes.private && !isViewPrivatePhotos)}
                alt={currentImage.label || 'user'}
              />
            </picture>
          </div>
          {item.type === PhotoTypes.private && !isViewPrivatePhotos
            ? <PrivateBox id={id} closeHandler={closeHandler} /> : null}
        </div>
      </SwiperSlide>
    ))
  }, [photos, currentImage])

  if (!isOpenGallery) return <></>

  return (
    <div className={styles.gallery}>
      {isPhotoList ? <div className={names(styles.photoList, styles.customScroll, isHiddenSideBar && styles.hidden)}>
        <h5 className={styles.groupTitle}>Photo</h5>
        <PhotoBlock load={load} isError={isError} isFetching={isFetching} />
      </div> : null}
      <div className={`${styles.photoPreview} ${!isPhotoList ? styles.onePhoto : ''}`}>
        <div className={names(styles.photoPreviewNav, styles.photoPreviewNavLeft)}>
          {isPhotoList ? <>
            <div className={styles.photoPreviewNavTop}>
              <div
                onClick={hideHandler}
                className={styles.hideIcon}
              >
                {isHiddenSideBar ? <ChevronsRight /> : <ChevronsLeft />}
              </div>
            </div>

            {currentImage.index !== 0 && photos.length > 1 && currentImage.id !== photos[0].id ?
              <div className={styles.photoPreview__arrow} onClick={() => onChangeHandler(false)}>
                <div className={styles.arrowIcon}>
                  <ArrowDown />
                </div>
              </div>
              : null}
          </> : null}
        </div>
        <div className={styles.photoContainer}>
          <div className={styles.photoContainerHeader}>
            <div
              onClick={closeHandler}
              className={styles.closeIcon}
            >
              <Cross />
            </div>
            <p className={styles.name}>{currentImage.label}</p>
            {!id ?
              <div className={styles.extra}>
                <div
                  className={styles.extraIcon}
                  onClick={() => setIsExtraOpen(true)}
                >
                  <Dots />
                </div>
                <>
                  <Backdrop
                    invisible
                    open={isExtraOpen}
                    onClick={() => setIsExtraOpen(false)}
                    className={styles.backdrop}
                  />
                  <div
                    className={names(
                      styles.dropdown,
                      !isExtraOpen && styles.hidden
                    )}
                  >
                    <div
                      className={styles.menuItem}
                      onClick={() => deleteHandler(currentImage.id)}
                    >
                      Delete
                      {fetchingDelete
                        ? <Loader size={20} className={styles.loaderDelete} />
                        : <TrashOutlined />
                      }
                    </div>
                  </div>
                </>
              </div>
              : null}
          </div>
          {!is768p ?
            <div
              className={styles.photoContainerBox}
              ref={photoContainerBoxRef}
            >
              <Loader size={20} innerColor='white' className={styles.photoContainerBoxLoader} />
              <picture className={styles.picture}>
                <img
                  className={`${styles.photo} ${isLoadingPhoto ? styles.loading : ''}`}
                  src={updateSrc(currentImage.src, currentImage.type, isPrivatePhoto)}
                  alt={currentImage.label || 'user'}
                  onLoad={() => setIsLoadingPhoto(false)}
                  onError={() => setIsLoadingPhoto(false)}
                />
              </picture>
              {isPrivatePhoto ? <PrivateBox id={id} closeHandler={closeHandler} /> : null}
              {id && !isHideLike && currentImage.type !== PhotoTypes.avatar && currentImage.type !== PhotoTypes.VIDEO_AVATAR ?
                <div className={styles.btnLikeWrapper}>
                  <LikeButton
                    className={styles.btnLike}
                    value={currentImage.id ? likePhotos[currentImage.id] : false}
                    onClick={() => likeHandler()}
                  />
                </div> : null}
            </div> : null}
          {is768p ?
            <>
              <Swiper
                onSwiper={setSwiperRef}
                slidesPerView={countSlidesPerView}
                className={styles.swiper}
                onSlideChange={onSlideChange}
                initialSlide={currentImage.index}
                onReachEnd={onReachEnd}
                loop={galleryCursor === null && swiperRef?.activeIndex !== 0}
                modules={[Zoom]}
                zoom
              >
                {renderedSlides}
              </Swiper>
              {id && !isHideLike && currentImage.type !== PhotoTypes.avatar && currentImage.type !== PhotoTypes.VIDEO_AVATAR ?
                <div className={styles.btnLikeWrapper}>
                  <LikeButton
                    className={styles.btnLike}
                    value={currentImage.id ? likePhotos[currentImage.id] : false}
                    onClick={() => likeHandler()}
                  />
                </div>
                : null}
            </>
            : null}
          <div className={styles.photoContainerFooter}></div>
        </div>
        <div className={names(styles.photoPreviewNav, styles.photoPreviewNavRight)}>
          <div className={styles.photoPreviewNavTop}>
            {!id ?
              <div
                onClick={() => deleteHandler(currentImage.id)}
                className={names(styles.closeIcon, styles.closeIconPc)}
              >
                {fetchingDelete ? <Loader innerColor='white' size={20} /> : <TrashOutlined />}
              </div>
              : null
            }
            <div
              onClick={closeHandler}
              className={names(styles.closeIcon, styles.closeIconPc, styles.cross )}
            >
              <Close />
            </div>
          </div>

          {isPhotoList && photos.length > 1 ? <div
            className={`${styles.photoPreview__arrow} ${styles.photoPreview__arrow_right}`}
            onClick={() => onChangeHandler(true)}
          >
            <div className={styles.arrowIcon}>
              <ArrowDown />
            </div>
          </div> : null}
        </div>
      </div>
    </div>
  )
}

type PhotoBlockProps = {
  load?: QueryObserverBaseResult['refetch']
  isFetching?: boolean
  isError?: boolean
}

const PhotoBlock: FC<PhotoBlockProps> = ({load, isFetching, isError}) => {
  const dispatch = useDispatch()
  const {galleryPhotos} = useGallery()
  const ref = useRef<HTMLDivElement>(null)
  const cursor = useGalleryCursor()
  const lastItem = createRef<HTMLDivElement>()

  load != null && isFetching != null && isError != null && useVirtualScroll({ref, isFetching, isError, cursor, load})

  const onClick = (src: string, index: number, type: PhotoTypes, id: string) => {
    dispatch(setCurrentImage({src, index, type, id}))
  }

  return (
    <div className={styles.photoBlock} ref={ref}>
      <div className={styles.privatePhotos}>
        {galleryPhotos.map((item, index) => (
          <div
            key={item.image.key}
            ref={index + 1 === galleryPhotos.length ? lastItem : null}
          >
            <Image
              onClick={() => onClick(item.image.url, index, item.type, item.id)}
              src={item.image.url}
              isPrivate={item.type === PhotoTypes.private}
            />
          </div>
        ))}
      </div>
      {isFetching ? <Loader className={styles.dndContainerLoader} /> : null}
    </div>
  )
}

type ImageProps = {
  src?: string
  isPrivate?: boolean
  onClick: () => void
}

const Image: FC<ImageProps> = ({src, onClick, isPrivate}) => {
  const isViewPrivatePhotos = useChatCanViewPrivatePhotos()
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const isPrivatePhoto = isPrivate && !isViewPrivatePhotos

  const onLoaded = () => {
    setIsLoading(false)
  }

  return (
    <picture
      onClick={onClick}
      className={names(styles.imagePreview, isPrivatePhoto ? styles.private : '')}
    >
      {isLoading ?
        <Skeleton
          classes={{root: styles.imagePreviewSkeleton}}
          variant='rectangular'
          animation='wave'
        /> : null
      }
      {!isError && <img
        className={styles.imagePreview__image}
        src={src + (isPrivatePhoto ? mediaVariants.square128blur : mediaVariants.square128)}
        alt='galleryImage'
        onLoad={onLoaded}
        onError={() => setIsError(true)}
      />}
    </picture>
  )
}

type PrivateBoxProps = {
  id?: string
  closeHandler: () => void
}

const PrivateBox: FC<PrivateBoxProps> = ({id, closeHandler}) => {
  const navigate = useNavigate()

  const chatNowHandler = () => {
    navigate(`/messages/${id}`)
    closeHandler()
  }

  return (
    <div className={styles.privateBox}>
      <div className={styles.privateBoxIcon}><Lock /></div>
      <p className={styles.privateBoxTitle}>
        To see more photos, start chat
      </p>
      <p className={styles.privateBoxText}>
        Photos will be unlocked after the answer
      </p>
      <Button
        styleType='transparentPrimary'
        className={styles.privateBoxBtn}
        onClick={chatNowHandler}
      >
        Chat now
        <PaperPlane />
      </Button>
    </div>
  )
}
